import { useQueries } from '@tanstack/react-query';
import React, { memo, useEffect, useMemo } from 'react';
import { getGeneralCareNotificationQueryOptions } from '../../api/get-general-care-notification.api';
import { DEFAULT_GENERAL_CARE_NOTIFICATION } from '@/app/settings/setting-notification-page/care-notification-tab/care-notification-tab.constant';
import { getGeneralTicketingNotificationQueryOptions } from '../../api/get-general-ticketing-notification.api';
import { DEFAULT_GENERAL_TICKETING_NOTIFICATION } from '@/app/settings/setting-notification-page/ticketing-notification-tab/ticketing-notification-tab.constant';
import { useNotificationStoreActions } from '../../stores/new-notification.store';
import useAuthContext from '@/features/auth/_shared/contexts/auth.context';

interface INotificationProviderProps {
  children: React.ReactNode;
}

/**
 * NotificationProvider - Production-grade provider for notification state management
 *
 * Features:
 * - Prevents memory leaks with isMounted pattern
 * - Optimized re-renders with useCallback and useMemo
 * - Proper cleanup of all subscriptions and intervals
 * - Handles route-based sound page detection
 * - Tracks browser visibility state
 * - Monitors browser notification permissions
 */
const NotificationProvider = memo(({ children }: INotificationProviderProps) => {
  const { isTicketingRole, hydrated, state, isAuthenticated } = useAuthContext();
  const { setIsOnActiveCaseSoundPage, setIsOnUnassignedCaseSoundPage, setIsOnTicketingSoundPage } =
    useNotificationStoreActions();

  // Ref to track mount status and prevent state updates after unmount
  // const isMountedRef = useRef(true);

  const [
    {
      data: generalCareNotification,
      // isLoading: _isLoadingGeneralCareNotification,
      // isFetching: _isFetchingGeneralCareNotification,
    },
    {
      data: generalTicketingNotification,
      // isLoading: _isLoadingGeneralTicketingNotification,
      // isFetching: _isFetchingGeneralTicketingNotification,
    },
  ] = useQueries({
    queries: [
      {
        ...getGeneralCareNotificationQueryOptions((data) => {
          if (data?.payload) {
            return {
              overall: data?.payload?.config?.overall,
              activeCaseSound: data?.payload?.config?.activeCaseSound,
              unassignedCaseSound: data?.payload?.config?.unassignedCaseSound,
              activeCount: data?.payload?.activeCount,
            };
          }
          return DEFAULT_GENERAL_CARE_NOTIFICATION;
        }),
        enabled: hydrated && !state?.loading && isAuthenticated && !isTicketingRole,
        staleTime: 0,
        gcTime: 0,
      },
      {
        ...getGeneralTicketingNotificationQueryOptions((data) => {
          if (data?.payload) {
            return {
              overall: data?.payload?.config?.overall,
              sound: data?.payload?.config?.sound,
              activeCount: data?.payload?.activeCount,
            };
          }
          return DEFAULT_GENERAL_TICKETING_NOTIFICATION;
        }),
        enabled: hydrated && !state?.loading && isAuthenticated,
        staleTime: 0,
        gcTime: 0,
      },
    ],
  });

  // Memoize route checks to prevent unnecessary recalculations
  // const routeStates = useMemo(() => {
  //   return {
  //     isOnActiveCasePage: pathname.includes('/active-cases'),
  //     isOnUnassignedCasePage: pathname.includes('/unassigned-cases'),
  //     isOnTicketingPage: pathname.includes('/ticketing'),
  //   };
  // }, [pathname]);

  // Memoize notification config flags to prevent unnecessary recalculations
  const notificationConfig = useMemo(() => {
    return {
      careOverallEnabled: generalCareNotification?.overall ?? false,
      careActiveCaseSoundEnabled: generalCareNotification?.activeCaseSound ?? false,
      careUnassignedCaseSoundEnabled: generalCareNotification?.unassignedCaseSound ?? false,
      ticketingOverallEnabled: generalTicketingNotification?.overall ?? false,
      ticketingSoundEnabled: generalTicketingNotification?.sound ?? false,
    };
  }, [
    generalCareNotification?.overall,
    generalCareNotification?.activeCaseSound,
    generalCareNotification?.unassignedCaseSound,
    generalTicketingNotification?.overall,
    generalTicketingNotification?.sound,
  ]);

  // Update route-based sound page states
  useEffect(() => {
    // if (!isMountedRef.current) return;

    // const { isOnActiveCasePage, isOnUnassignedCasePage, isOnTicketingPage } = routeStates;
    const {
      careOverallEnabled,
      careActiveCaseSoundEnabled,
      careUnassignedCaseSoundEnabled,
      ticketingOverallEnabled,
      ticketingSoundEnabled,
    } = notificationConfig;

    // Only set to true if on the page AND sound is enabled in config
    setIsOnActiveCaseSoundPage(careActiveCaseSoundEnabled && careOverallEnabled);
    setIsOnUnassignedCaseSoundPage(careUnassignedCaseSoundEnabled && careOverallEnabled);
    setIsOnTicketingSoundPage(ticketingSoundEnabled && ticketingOverallEnabled);
  }, [notificationConfig, setIsOnActiveCaseSoundPage, setIsOnUnassignedCaseSoundPage, setIsOnTicketingSoundPage]);

  // // Memoized visibility computation function
  // const computeVisibility = useCallback(() => {
  //   if (!isMountedRef.current) return;
  //   const isVisible = document.visibilityState === 'visible' || document.hasFocus();
  //   setIsUserSameTab(isVisible);
  // }, [setIsUserSameTab]);

  // // Memoized focus handler
  // const handleFocus = useCallback(() => {
  //   if (!isMountedRef.current) return;
  //   setIsUserSameTab(true);
  // }, [setIsUserSameTab]);

  // // Memoized blur handler
  // const handleBlur = useCallback(() => {
  //   computeVisibility();
  // }, [computeVisibility]);

  // // Update browser visibility state
  // useEffect(() => {
  //   computeVisibility();
  //   document.addEventListener('visibilitychange', computeVisibility);
  //   window.addEventListener('focus', handleFocus);
  //   window.addEventListener('blur', handleBlur);

  //   return () => {
  //     document.removeEventListener('visibilitychange', computeVisibility);
  //     window.removeEventListener('focus', handleFocus);
  //     window.removeEventListener('blur', handleBlur);
  //   };
  // }, [computeVisibility, handleFocus, handleBlur]);

  // Ref to track previous permission state for change detection
  // const previousPermissionRef = useRef<string | null>(null);

  // Memoized browser notification check function
  // const checkBrowserNotification = useCallback(() => {
  //   if (!isMountedRef.current) return;

  //   const hasNotificationAPI = 'Notification' in window;
  //   const currentPermission = hasNotificationAPI ? Notification.permission : 'denied';
  //   const isGranted = currentPermission === 'granted';

  //   // Detect permission changes
  //   const previousPermission = previousPermissionRef.current;
  //   if (previousPermission !== null && previousPermission !== currentPermission) {
  //     // Permission changed - could trigger additional logic here if needed
  //     // For example: show toast, update UI, etc.
  //   }

  //   previousPermissionRef.current = currentPermission;
  //   setIsOnBrowserNotification(isGranted);
  // }, [setIsOnBrowserNotification]);

  // Update browser notification permission state
  // Handles real-time permission changes when user toggles in browser settings
  // useEffect(() => {
  //   // Initial check
  //   checkBrowserNotification();

  //   // Check on visibility change (user might have changed browser settings)
  //   // Check both when visible and hidden to catch changes immediately
  //   const handleVisibilityChange = () => {
  //     checkBrowserNotification();
  //   };

  //   // Check on window focus (user might have changed settings and returned)
  //   const handleWindowFocus = () => {
  //     checkBrowserNotification();
  //   };

  //   // Check on user interaction (clicks, etc.) - user might have just changed settings
  //   // Using a throttled approach to avoid excessive checks
  //   let interactionTimeout: NodeJS.Timeout | null = null;
  //   const handleUserInteraction = () => {
  //     if (interactionTimeout) {
  //       clearTimeout(interactionTimeout);
  //     }
  //     // Throttle: check after 500ms of no interactions
  //     interactionTimeout = setTimeout(() => {
  //       checkBrowserNotification();
  //     }, 500);
  //   };

  //   document.addEventListener('visibilitychange', handleVisibilityChange);
  //   window.addEventListener('focus', handleWindowFocus);
  //   // Listen to common user interactions
  //   document.addEventListener('click', handleUserInteraction, true);
  //   document.addEventListener('keydown', handleUserInteraction, true);

  //   // Periodic check with shorter interval (10 seconds) for better responsiveness
  //   // This ensures we catch permission changes even if events don't fire
  //   const interval = setInterval(checkBrowserNotification, 10000);

  //   return () => {
  //     document.removeEventListener('visibilitychange', handleVisibilityChange);
  //     window.removeEventListener('focus', handleWindowFocus);
  //     document.removeEventListener('click', handleUserInteraction, true);
  //     document.removeEventListener('keydown', handleUserInteraction, true);
  //     if (interactionTimeout) {
  //       clearTimeout(interactionTimeout);
  //     }
  //     clearInterval(interval);
  //   };
  // }, [checkBrowserNotification]);

  // Cleanup on unmount
  // useEffect(() => {
  //   return () => {
  //     isMountedRef.current = false;
  //   };
  // }, []);

  return <>{children}</>;
});
NotificationProvider.displayName = 'NotificationProvider';

export default NotificationProvider;
